import { XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useCallback, useState } from 'react'
import { DRAWER_SLIDE_ANIMATION_DURATION_MS } from '../../components/drawers/drawer-constants.ts'
import { getEnvironmentData } from '../../utils/hooks/get-environment-data.ts'
import { RISK_DIMENSIONS, riskScoreSummary, riskVersusPostureNote } from './risk-dimensions.ts'
import * as styles from './risk-reference-drawer.css.ts'

export type RiskReferenceDrawerProps = {
  onClose: () => unknown
}

/**
 * What the risk score is made of, opened from the question icon in the table's RISK header.
 *
 * Built on the same reference-drawer surface as `OutcomesReferenceDrawer` — the app already documents one
 * table column that way ("A guide to analysis outcomes", behind a question icon in the findings table
 * header), and this documents another. It explains the *column*, not any one repository, which is why it
 * hangs off the header rather than off a row or the per-repository risk drawer.
 *
 * **`open` tracks local state rather than being a literal `true`.** The consumer mounts this
 * conditionally, so calling `onClose` straight away tore the subtree down on the next render — Radix never
 * set `data-state="closed"`, and the `slideOut` keyframe the stylesheet defines could never run. Closing
 * now flips `open` first, which lets that animation play, and the parent unmounts once it has.
 */
export const RiskReferenceDrawer: React.FC<RiskReferenceDrawerProps> = ({ onClose }) => {
  const [isClosing, setIsClosing] = useState(false)
  const beginClose = useCallback(() => {
    setIsClosing(true)
    // The shared drawer constant, so this cannot drift from the duration the stylesheet animates for.
    setTimeout(onClose, DRAWER_SLIDE_ANIMATION_DURATION_MS)
  }, [onClose])

  return (
    <Dialog.Root
      open={!isClosing}
      modal
      onOpenChange={open => {
        // One close path for the X, Escape and an overlay click alike.
        if (!open) {
          beginClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} aria-describedby={undefined}>
          <div className={styles.titleRow}>
            <Dialog.Title className={styles.title}>About risk</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className={styles.closeButton} aria-label="Close">
                <XIcon className={styles.closeIcon} weight="bold" />
              </button>
            </Dialog.Close>
          </div>

          <p className={styles.summary}>{riskScoreSummary(getEnvironmentData().productName)}</p>

          <p className={styles.note}>
            <span className={styles.noteLabel}>Note: </span>
            {riskVersusPostureNote()}
          </p>

          <ul className={styles.dimensionList}>
            {RISK_DIMENSIONS.map(dimension => (
              <li key={dimension.id} className={styles.dimension}>
                <p className={styles.dimensionName}>{dimension.name}</p>
                <p className={styles.dimensionGloss}>{dimension.gloss}</p>
              </li>
            ))}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
